import React, { useCallback, useEffect, useState } from "react";
import { admin } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
};

const useLoad = (fn) => {
  const [data, setData] = useState(null);
  const refresh = useCallback(() => fn().then(setData).catch(() => setData([])), [fn]);
  useEffect(() => { refresh(); }, [refresh]);
  return [data, refresh];
};

const loadOpen = () => admin.alerts("open");
const loadAll = () => admin.alerts("");
const loadBatches = () => admin.recoveryBatches();

export const AlertBanner = () => {
  const [alerts] = useLoad(loadOpen);
  if (!alerts || alerts.length === 0) return null;
  const critical = alerts.some((a) => a.severity === "critical");
  return (
    <div
      data-testid="delivery-alert-banner"
      className={`max-w-7xl mx-auto mt-4 px-6 md:px-10 py-3 border flex items-start gap-3 ${
        critical ? "border-red-500/60 bg-red-500/10 text-red-200" : "border-amber-400/50 bg-amber-400/10 text-amber-100"
      }`}
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="text-sm">
        <p className="font-medium" data-testid="delivery-alert-banner-count">
          {alerts.length} open delivery alert{alerts.length > 1 ? "s" : ""}
        </p>
        <p className="opacity-80" data-testid="delivery-alert-banner-kinds">
          {alerts.map((a) => `${a.kind} (${a.channel}${a.scope ? " " + a.scope : ""}) ×${a.count}`).join(" · ")}
        </p>
      </div>
    </div>
  );
};

export const AlertHistory = () => {
  const [alerts, refresh] = useLoad(loadAll);
  const act = async (id, action) => {
    try { await admin.alertAction(id, action); refresh(); } catch (e) { toast.error(errMsg(e, "Action failed")); }
  };
  return (
    <section className="mb-8" data-testid="alert-history">
      <p className="label-mono text-terracotta mb-3">Delivery alerts</p>
      {!alerts ? <Loader2 className="w-4 h-4 animate-spin" /> : alerts.length === 0 ? (
        <p className="text-sm text-white/50" data-testid="alert-history-empty">No delivery alerts.</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {alerts.map((a) => (
              <tr key={a.id} className="border-b border-white/5" data-testid={`alert-row-${a.id}`}>
                <td className="py-2 pr-3">{a.severity}</td>
                <td className="pr-3">{a.kind}</td>
                <td className="pr-3">{a.channel}{a.scope ? ` ${a.scope}` : ""}</td>
                <td className="pr-3">×{a.count}</td>
                <td className="pr-3 text-white/50">{a.last_seen?.slice(0, 16).replace("T", " ")}</td>
                <td className="pr-3" data-testid={`alert-status-${a.id}`}>{a.status}</td>
                <td className="text-right space-x-2">
                  {a.status === "open" && (
                    <Button size="sm" variant="outline" onClick={() => act(a.id, "acknowledge")} data-testid={`alert-ack-${a.id}`}>Acknowledge</Button>
                  )}
                  {a.status !== "resolved" && (
                    <Button size="sm" variant="outline" onClick={() => act(a.id, "resolve")} data-testid={`alert-resolve-${a.id}`}>Resolve</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

const BatchRow = ({ b, onChange }) => {
  const approve = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Approve batch ${b.id}? Only the listed failed channels will be attempted.`)) return;
    try { await admin.approveRecoveryBatch(b.id); onChange(); } catch (e) { toast.error(errMsg(e, "Approve failed")); }
  };
  const runIt = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Run approved batch ${b.id} now? This sends real notifications in production.`)) return;
    try { const r = await admin.runRecoveryBatch(b.id); toast.success(`Batch ${r.status}`); onChange(); }
    catch (e) { toast.error(errMsg(e, "Run failed")); onChange(); }
  };
  return (
    <div className="border border-white/10 p-3 mb-2" data-testid={`recovery-batch-${b.id}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm">
          <span className="font-mono">{b.id}</span> · <span data-testid={`recovery-batch-status-${b.id}`}>{b.status}</span>
          {b.halt_reason ? ` · halted: ${b.halt_reason}` : ""}
        </p>
        <div className="space-x-2">
          {b.status === "draft" && <Button size="sm" onClick={approve} data-testid={`recovery-approve-${b.id}`}>Approve</Button>}
          {b.status === "approved" && <Button size="sm" onClick={runIt} data-testid={`recovery-run-${b.id}`}>Run</Button>}
        </div>
      </div>
      <ul className="mt-2 text-xs text-white/60 space-y-0.5">
        {b.items.map((it) => (
          <li key={`${it.capsule_id}-${it.channel}`} data-testid={`recovery-item-${it.capsule_id}-${it.channel}`}>
            {it.capsule_id.slice(0, 8)} · {it.channel} · {it.state}{it.reason ? ` (${it.reason})` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const RecoveryPanel = () => {
  const [batches, refresh] = useLoad(loadBatches);
  const draft = async () => {
    try { await admin.createRecoveryBatch({ limit: 10 }); refresh(); } catch (e) { toast.error(errMsg(e, "Nothing to recover")); }
  };
  return (
    <section className="mb-8" data-testid="recovery-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="label-mono text-terracotta">Failed-notification recovery (admin approval required)</p>
        <Button size="sm" variant="outline" onClick={draft} data-testid="recovery-create-draft">Prepare draft batch</Button>
      </div>
      {!batches ? <Loader2 className="w-4 h-4 animate-spin" /> : batches.length === 0 ? (
        <p className="text-sm text-white/50" data-testid="recovery-empty">No recovery batches yet.</p>
      ) : batches.slice(0, 5).map((b) => <BatchRow key={b.id} b={b} onChange={refresh} />)}
    </section>
  );
};
